import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowUp,
  ChevronDown,
  Circle,
  Paperclip,
  Pin,
  PinOff,
  Star,
  Redo2,
  Search,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import {
  ARKO_HUB_MODES_LEGACY,
  ARKO_INTENT_EVENT,
  dispatchArkoHubIntent,
  type ArkoHubMode,
} from './arkoHubModes';
import { ArkoComposerSearchChip } from './ArkoComposerSearchChip';
import { ArkoComposerJudSearchChip } from './ArkoComposerJudSearchChip';
import { ArkoComposerWebSearchChip } from './ArkoComposerWebSearchChip';
import type { ParagraphRef } from './portalParagraphRef';
import {
  COMPOSER_TOOL_PIN_CATALOG,
  getComposerPinMeta,
  isComposerPinId,
  loadComposerPins,
  modeIdToPinId,
  saveComposerPins,
  toggleComposerPin,
  type ComposerPinId,
  type ComposerToolPinId,
} from './portalComposerPins';
import { CURSOR_PORTAL } from '../arkorbit-ui/portalTheme';
import type { PortalChipBarPinControls } from '../arkorbit-ui/PortalChipBar';
import {
  isPortalImageFile,
  PORTAL_DOCUMENT_FILE_ACCEPT,
  PORTAL_IMAGE_OCR_NOTICE,
} from './portalDocuments';
import {
  canPortalChatAnalyzeUpload,
  formatPortalFileSize,
  PORTAL_CHAT_ANALYZE_MAX_FILES,
  validatePortalChatAnalyzeFile,
} from './portalChatDocumentAttach';
import { ArkoContextMeter } from './ArkoContextMeter';
import { ARKO_CHAT_COLUMN_CLASS } from './portalArkoChatWidth';
import {
  buildArkoContextUsage,
  type ArkoContextLexSource,
  type ArkoContextMemItem,
  type ArkoContextMessage,
} from './arkoContextWindow';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';

export type ArkoCursorComposerHandle = {
  collapseTrays: () => void;
};

export interface ArkoCursorComposerProps {
  customerName?: string;
  kanzlei?: string;
  placeholder?: string;
  compact?: boolean;
  autoFocus?: boolean;
  /** `center` = leerer Chat; `bottom` = Composer am unteren Rand. */
  layout?: 'center' | 'bottom';
  /** Controlled-Modus für laufenden Chat (Intent-Chips, Draft). */
  value?: string;
  onChange?: (value: string) => void;
  onSubmit: (text: string, mode?: ArkoHubMode, attachments?: File[]) => void;
  onModeSelect?: (mode: ArkoHubMode) => void;
  /** Intent-Chips / Werkzeuge — z. B. PortalChipBar-Inhalt (wiederverwendet). */
  toolsBar?: React.ReactNode;
  showToolsTray?: boolean;
  /** Laufende ARKO-Anfrage — Senden-Button wird zu Abbrechen. */
  isLoading?: boolean;
  onCancel?: () => void;
  /** Angeheftete §§ aus der Paragraphen-Suchleiste (Kontext, kein Tray-Pin). */
  attachedParagraphs?: ParagraphRef[];
  onAttachedParagraphsChange?: (next: ParagraphRef[]) => void;
  /** Sofort-Nachschlagen über rechtsfrage / lookup-Pfad. */
  onParagraphLookup?: (refs: ParagraphRef[], prompt: string) => void;
  /** Suchleisten-Chip OGH → judicature_recherche /v1/jud/answer (Premium-IF in n8n). */
  onJudicatureLookup?: (query: string) => void;
  /** Composer-Chip Recherche → web_recherche / ARKORBIT-Web-Search-Gate (keine Akte). */
  onWebSearchLookup?: (query: string) => void;
  /** Verwandte §§ aus letztem lexProof.norm_chain (Suchleisten-Related). */
  relatedParagraphs?: ParagraphRef[];
  /**
   * Optional: User-ID für Pin-Prefs (localStorage-Key).
   * Ohne Session → Key `…:local`. Keine PII in den Prefs selbst.
   */
  pinsUserId?: string | null;
  /** Nächster Ollama-Prompt (Bausteine) — Kreis in der Composer-Leiste. */
  contextMeter?: {
    messages?: ArkoContextMessage[];
    documentContext?: string | null;
    extractTitles?: string[];
    pendingIntent?: string | null;
    memItems?: ArkoContextMemItem[];
    lexSources?: ArkoContextLexSource[];
    akteHandoff?: string | null;
    /** Recherche-Karte (≤400 Tokens) — nur wenn das Gate eine Karte geliefert hat. */
    webCard?: string | null;
  };
  /** Kreis voll / Weiterarbeiten an derselben Akte (nicht Plus). */
  onContinueAkte?: () => void;
  /** Gespeicherter Modus dieses Chats. Unbekannte Id wird ignoriert. */
  savedModeId?: string | null;
  onModeCommit?: (modeId: string) => void;
  /** Hauptreiter: schwarzer Grund, lila Kante, Würfel-Drehung der Akte. */
  appearance?: 'haupt';
}

const TRAY_PILL =
  'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-slate-900/70 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:border-violet-500/30 hover:bg-violet-500/5 hover:text-slate-100 transition-colors';

const TRAY_PILL_ACTIVE =
  'inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-violet-400/25';

/** Dieselbe Würfel-Drehung wie Arko ON/off in der Akte. */
function HauptTrayFlip({
  open,
  label,
  onClick,
  testId,
  expanded,
}: {
  open: boolean;
  label: string;
  onClick: () => void;
  testId?: string;
  expanded?: 'listbox' | 'true';
}) {
  const face =
    'absolute inset-0 flex h-full w-full items-center justify-center whitespace-nowrap px-2 text-[11px] font-semibold [backface-visibility:hidden]';
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-expanded={open}
      aria-haspopup={expanded}
      aria-label={label}
      className="relative h-7 shrink-0 [perspective:280px]"
      style={{ width: `max(5.75rem, ${label.length + 1}ch)` }}
    >
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-100`}>
          {label}
        </span>
        <span
          className={`${face} rounded-md bg-violet-600 text-white`}
          style={{ transform: 'rotateX(180deg)' }}
        >
          {label}
        </span>
      </span>
    </button>
  );
}

/**
 * ARKO-Composer — Cursor-Stil: Modus- + Werkzeuge-Reiter direkt im Composer.
 * Suchleiste (Paragraph suchen + §-Favoriten + Related) und Modus-/Tool-Pin-Tray parallel.
 * Verfahrensmotor Slice 1: strukturierte Bot-Antworten `messageKind: approval_frist`
 * werden in `InlineChat` als `FristApprovalCard` gerendert (nicht im Composer).
 */
export const ArkoCursorComposer = forwardRef<ArkoCursorComposerHandle, ArkoCursorComposerProps>(
  (
    {
      customerName,
      kanzlei,
      placeholder = 'Frag ARKO zu Recht, Text oder Dokumenten…',
      compact = false,
      autoFocus = true,
      layout = 'center',
      value: controlledValue,
      onChange: controlledOnChange,
      onSubmit,
      onModeSelect,
      toolsBar,
      showToolsTray = false,
      isLoading = false,
      onCancel,
      attachedParagraphs = [],
      onAttachedParagraphsChange,
      onParagraphLookup,
      onJudicatureLookup,
      onWebSearchLookup,
      relatedParagraphs = [],
      pinsUserId = null,
      contextMeter,
      onContinueAkte,
      savedModeId = null,
      onModeCommit,
      appearance,
    },
    ref,
  ) => {
    const [internalText, setInternalText] = useState('');
    const text = controlledValue !== undefined ? controlledValue : internalText;
    const setText = controlledOnChange ?? setInternalText;
    const [activeModeId, setActiveModeId] = useState<string | null>(null);
    /** Werkzeug-Pin als Submit-Intent (Empty-State ohne InlineChat-pendingIntent). */
    const [activeToolMode, setActiveToolMode] = useState<ArkoHubMode | null>(null);
    const [openTray, setOpenTray] = useState<'modus' | 'werkzeuge' | null>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [attachError, setAttachError] = useState<string | null>(null);
    const [pinnedIds, setPinnedIds] = useState<ComposerPinId[]>(() =>
      loadComposerPins(pinsUserId),
    );
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const modusRootRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const id = savedModeId?.trim() || '';
      if (!id || !ARKO_HUB_MODES_LEGACY.some((mode) => mode.id === id)) {
        setActiveModeId(null);
        return;
      }
      setActiveModeId(id);
      setActiveToolMode(null);
    }, [savedModeId]);
    const werkzeugeRootRef = useRef<HTMLDivElement>(null);

    const collapseTrays = useCallback(() => setOpenTray(null), []);

    useImperativeHandle(ref, () => ({ collapseTrays }), [collapseTrays]);

    usePopoverDismissOutside({
      open: openTray === 'modus',
      onClose: collapseTrays,
      rootRef: modusRootRef,
    });
    usePopoverDismissOutside({
      open: openTray === 'werkzeuge',
      onClose: collapseTrays,
      rootRef: werkzeugeRootRef,
    });

    useEffect(() => {
      setPinnedIds(loadComposerPins(pinsUserId));
    }, [pinsUserId]);

    const persistPins = useCallback(
      (next: ComposerPinId[]) => {
        setPinnedIds(next);
        saveComposerPins(next, pinsUserId);
      },
      [pinsUserId],
    );

    const handleTogglePin = useCallback(
      (id: ComposerPinId) => {
        persistPins(toggleComposerPin(pinnedIds, id));
      },
      [persistPins, pinnedIds],
    );

    const handleTogglePinFromBar = useCallback(
      (rawId: string) => {
        if (!isComposerPinId(rawId)) return;
        handleTogglePin(rawId);
      },
      [handleTogglePin],
    );

    const activeMode = ARKO_HUB_MODES_LEGACY.find((m) => m.id === activeModeId);
    const isAnalyzeMode = activeModeId === 'analysieren';
    const canAttachFiles = isAnalyzeMode && canPortalChatAnalyzeUpload();
    const canSubmit = Boolean(text.trim()) || (isAnalyzeMode && attachments.length > 0);

    useEffect(() => {
      if (!isAnalyzeMode) {
        setAttachments([]);
        setAttachError(null);
      }
    }, [isAnalyzeMode]);

    const addAttachmentFiles = useCallback((fileList: FileList | null) => {
      if (!fileList?.length) return;
      setAttachError(null);
      const incoming = Array.from(fileList);
      setAttachments((prev) => {
        const next = [...prev];
        for (const file of incoming) {
          if (next.length >= PORTAL_CHAT_ANALYZE_MAX_FILES) {
            setAttachError(`Maximal ${PORTAL_CHAT_ANALYZE_MAX_FILES} Dateien pro Analyse.`);
            break;
          }
          const err = validatePortalChatAnalyzeFile(file);
          if (err) {
            setAttachError(err);
            continue;
          }
          if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
          next.push(file);
        }
        return next;
      });
    }, []);

    const removeAttachment = useCallback((index: number) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
      setAttachError(null);
    }, []);

    const handleMode = useCallback(
      (mode: ArkoHubMode) => {
        setActiveModeId(mode.id);
        setActiveToolMode(null);
        setOpenTray(null);
        onModeSelect?.(mode);
        onModeCommit?.(mode.id);
        dispatchArkoHubIntent(mode);
        const prefix = mode.promptPrefix ?? '';
        setText(text.trim() ? text : prefix);
        textareaRef.current?.focus();
      },
      [onModeSelect, onModeCommit, setText, text],
    );

    const clearHauptMode = useCallback(() => {
      const prefix = (activeMode?.promptPrefix || activeToolMode?.promptPrefix || '').trim();
      setActiveModeId(null);
      setActiveToolMode(null);
      setOpenTray(null);
      onModeCommit?.('');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(ARKO_INTENT_EVENT, {
          detail: { clear: true, promptPrefix: prefix },
        }));
      }
      textareaRef.current?.focus();
    }, [activeMode, activeToolMode, onModeCommit]);

    const activateToolPin = useCallback(
      (pinId: ComposerToolPinId) => {
        const meta = getComposerPinMeta(pinId);
        if (!meta?.intent) return;
        const prefix = meta.promptPrefix ?? '';
        const toolMode: ArkoHubMode = {
          id: 'fragen',
          label: meta.label,
          description: meta.label,
          icon: ARKO_HUB_MODES_LEGACY[0]?.icon ?? Sparkles,
          intent: meta.intent,
          promptPrefix: prefix,
          accent: 'violet',
        };
        setActiveModeId(null);
        setActiveToolMode(toolMode);
        setOpenTray(null);
        setText(text.trim() ? text : prefix);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(ARKO_INTENT_EVENT, {
              detail: {
                intent: meta.intent,
                promptPrefix: prefix,
                modeId: 'fragen',
              },
            }),
          );
        }
        textareaRef.current?.focus();
      },
      [setText, text],
    );

    const activatePinned = useCallback(
      (pinId: ComposerPinId) => {
        const meta = getComposerPinMeta(pinId);
        if (!meta) return;
        if (meta.kind === 'mode' && meta.modeId) {
          const mode = ARKO_HUB_MODES_LEGACY.find((m) => m.id === meta.modeId);
          if (mode) handleMode(mode);
          return;
        }
        if (meta.kind === 'tool' && meta.intent) {
          activateToolPin(pinId as ComposerToolPinId);
        }
      },
      [activateToolPin, handleMode],
    );

    const toggleTray = useCallback((tray: 'modus' | 'werkzeuge') => {
      setOpenTray((prev) => (prev === tray ? null : tray));
    }, []);

    const handleSubmit = useCallback(
      (e?: React.FormEvent) => {
        e?.preventDefault();
        if (isLoading) return;
        const trimmed = text.trim();
        const filesToSend = isAnalyzeMode ? attachments : [];
        if (!trimmed && filesToSend.length === 0) return;
        const mode =
          activeToolMode ?? ARKO_HUB_MODES_LEGACY.find((m) => m.id === activeModeId);
        onSubmit(trimmed, mode ?? undefined, filesToSend.length > 0 ? filesToSend : undefined);
        if (controlledOnChange) controlledOnChange('');
        else setInternalText('');
        setAttachments([]);
        setAttachError(null);
        setActiveModeId(null);
        setActiveToolMode(null);
        setOpenTray(null);
      },
      [
        text,
        activeModeId,
        activeToolMode,
        attachments,
        isAnalyzeMode,
        onSubmit,
        controlledOnChange,
        isLoading,
      ],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        setOpenTray(null);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (!isLoading) handleSubmit();
      }
    };

    const contextUsage = useMemo(() => {
      if (!contextMeter) return null;
      const modeIntent =
        activeToolMode?.intent
        || ARKO_HUB_MODES_LEGACY.find((m) => m.id === activeModeId)?.intent
        || contextMeter.pendingIntent
        || '';
      return buildArkoContextUsage({
        messages: contextMeter.messages,
        documentContext: contextMeter.documentContext,
        extractTitles: contextMeter.extractTitles,
        draft: text,
        intent: modeIntent,
        memItems: contextMeter.memItems,
        lexSources: contextMeter.lexSources,
        akteHandoff: contextMeter.akteHandoff,
        webCard: contextMeter.webCard,
      });
    }, [activeModeId, activeToolMode, contextMeter, text]);

    const pinControls: PortalChipBarPinControls = useMemo(
      () => ({
        pinnedIds,
        onTogglePin: handleTogglePinFromBar,
        schnellItems: COMPOSER_TOOL_PIN_CATALOG.filter((p) => p.toolsTab === 'schnell').map(
          (p) => ({ id: p.id, label: p.label }),
        ),
        rechtItems: COMPOSER_TOOL_PIN_CATALOG.filter((p) => p.toolsTab === 'recht').map((p) => ({
          id: p.id,
          label: p.label,
        })),
      }),
      [handleTogglePinFromBar, pinnedIds],
    );

    const toolsBarWithPins = useMemo(() => {
      if (!toolsBar || !isValidElement(toolsBar)) return toolsBar;
      return cloneElement(toolsBar as React.ReactElement<{ pinControls?: PortalChipBarPinControls }>, {
        pinControls,
      });
    }, [pinControls, toolsBar]);

    const firstName = customerName?.trim() || 'willkommen';
    const isBottom = layout === 'bottom';
    const showLanding = !isBottom && !compact;
    const hasTools = showToolsTray && Boolean(toolsBar);
    const composerMinPx = 56;

    const resizeComposerTextarea = useCallback(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      const maxPx = Math.round(window.innerHeight * 0.4);
      const next = Math.min(Math.max(el.scrollHeight, composerMinPx), maxPx);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxPx ? 'auto' : 'hidden';
    }, [composerMinPx]);

    useLayoutEffect(() => {
      resizeComposerTextarea();
    }, [text, resizeComposerTextarea, compact, isBottom]);

    useEffect(() => {
      const onResize = () => resizeComposerTextarea();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, [resizeComposerTextarea]);

    const hauptLook = appearance === 'haupt';
    const trayPopoverClass = hauptLook
      ? 'absolute bottom-full left-0 mb-1.5 z-30 min-w-[min(100vw-2rem,20rem)] max-w-[min(100vw-2rem,28rem)] rounded-md border border-violet-400/40 bg-[#07070a] shadow-[0_16px_36px_rgba(0,0,0,0.45)] overflow-hidden'
      : 'absolute bottom-full left-0 mb-1.5 z-30 min-w-[min(100vw-2rem,20rem)] max-w-[min(100vw-2rem,28rem)] rounded-xl border border-white/[0.1] bg-[#141619] shadow-[0_-8px_32px_rgba(0,0,0,0.45)] overflow-hidden';
    const pill = hauptLook
      ? 'inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-violet-600/30 px-2 py-1 text-[11px] font-semibold text-violet-100 hover:bg-violet-600/55'
      : TRAY_PILL;
    const pillOn = hauptLook
      ? 'inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500'
      : TRAY_PILL_ACTIVE;

    const composerBox = (
      <form
        onSubmit={handleSubmit}
        className="w-full"
      >
        <div
          className={`${
            hauptLook
              ? 'rounded-md border border-violet-400/40 bg-[#07070a] focus-within:border-violet-400'
              : `${CURSOR_PORTAL.composer} ${CURSOR_PORTAL.composerFocus} transition-shadow`
          } ${compact || isBottom ? 'p-3' : 'p-4 md:p-5'}`}
        >
          <textarea
            ref={textareaRef}
            data-portal-arko-input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            autoFocus={autoFocus}
            className="w-full bg-transparent text-[#e6e7ea] text-[15px] leading-relaxed placeholder:text-slate-600 focus:outline-none resize-none min-h-[56px] max-h-[40vh] overflow-y-auto custom-scrollbar"
          />

          {attachments.length > 0 ? (
            <ul
              className="flex flex-wrap gap-1.5 mt-2 max-h-28 overflow-y-auto custom-scrollbar portal-arko-composer-scroll"
              aria-label="Angehängte Dokumente"
            >
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${file.size}-${index}`}
                  className="inline-flex items-center gap-1.5 max-w-full rounded-lg border border-white/[0.08] bg-slate-900/60 px-2 py-1 text-[11px] text-slate-300"
                >
                  <span className="truncate max-w-[12rem]" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-slate-500 shrink-0">{formatPortalFileSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    disabled={isLoading}
                    className="p-0.5 rounded text-slate-500 hover:text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
                    aria-label={`${file.name} entfernen`}
                  >
                    <X className="w-3 h-3" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {attachError ? (
            <p className="mt-1.5 text-[11px] text-amber-400/90" role="alert">
              {attachError}
            </p>
          ) : null}

          {attachments.some((file) =>
            isPortalImageFile({ name: file.name, mimeType: file.type })) ? (
            <p className="mt-1.5 text-[10px] leading-relaxed text-amber-300/70">
              {PORTAL_IMAGE_OCR_NOTICE}
            </p>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={PORTAL_DOCUMENT_FILE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              addAttachmentFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {/* Cursor-Stil: Modus + Suchleiste + Pins + Werkzeuge */}
          <div className={`flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t ${hauptLook ? 'border-violet-400/30' : 'border-white/[0.06]'}`}>
            <div className="relative flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
              {/* Modus-Reiter */}
              <div className="relative" ref={modusRootRef}>
                {hauptLook ? (
                  <HauptTrayFlip
                    open={openTray === 'modus'}
                    label={activeMode?.label || activeToolMode?.label || 'Anfrage'}
                    onClick={() => toggleTray('modus')}
                    testId="portal-chip-modus"
                    expanded="listbox"
                  />
                ) : (
                <button
                  type="button"
                  data-testid="portal-chip-modus"
                  onClick={() => toggleTray('modus')}
                  aria-expanded={openTray === 'modus'}
                  aria-haspopup="listbox"
                  className={activeMode || activeToolMode ? pillOn : pill}
                >
                  {activeMode
                    ? activeMode.label
                    : activeToolMode
                      ? activeToolMode.label
                      : 'Modus'}
                  <ChevronDown
                    className={`w-3 h-3 shrink-0 transition-transform ${
                      openTray === 'modus' ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>
                )}
                {openTray === 'modus' ? (
                  <div className={trayPopoverClass} role="listbox" aria-label="ARKO-Modus wählen">
                    <div className="p-1.5 space-y-0.5 max-h-[min(60vh,320px)] overflow-y-auto custom-scrollbar">
                      {hauptLook ? (
                        <div
                          className={`flex items-stretch gap-0.5 rounded-lg ${
                            !activeModeId && !activeToolMode ? CURSOR_PORTAL.chipActive : 'hover:bg-white/[0.04] text-slate-300'
                          }`}
                        >
                          <button
                            type="button"
                            role="option"
                            data-testid="portal-chip-modus-anfrage"
                            aria-selected={!activeModeId && !activeToolMode}
                            onClick={clearHauptMode}
                            className="flex-1 min-w-0 text-left rounded-lg px-2.5 py-2 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Circle className="w-3.5 h-3.5 shrink-0 text-violet-400" aria-hidden />
                              <span className="text-xs font-medium text-slate-200">Anfrage</span>
                            </div>
                            {!compact ? (
                              <p className="text-[10px] text-slate-500 mt-0.5 pl-5 leading-snug line-clamp-2">
                                Freie Frage, ohne Sonderaktion.
                              </p>
                            ) : null}
                          </button>
                        </div>
                      ) : null}
                      {ARKO_HUB_MODES_LEGACY.map((mode) => {
                        const Icon = mode.icon;
                        const selected = activeModeId === mode.id;
                        const pinId = modeIdToPinId(mode.id);
                        const pinned = pinId ? pinnedIds.includes(pinId) : false;
                        return (
                          <div
                            key={mode.id}
                            className={`flex items-stretch gap-0.5 rounded-lg ${
                              selected ? CURSOR_PORTAL.chipActive : 'hover:bg-white/[0.04] text-slate-300'
                            }`}
                          >
                            <button
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => handleMode(mode)}
                              className="flex-1 min-w-0 text-left rounded-lg px-2.5 py-2 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 shrink-0 text-violet-400" aria-hidden />
                                <span className="text-xs font-medium text-slate-200">{mode.label}</span>
                              </div>
                              {!compact && (
                                <p className="text-[10px] text-slate-500 mt-0.5 pl-5 leading-snug line-clamp-2">
                                  {mode.description}
                                </p>
                              )}
                            </button>
                            {pinId ? (
                              <button
                                type="button"
                                onClick={() => handleTogglePin(pinId)}
                                aria-pressed={pinned}
                                aria-label={
                                  pinned
                                    ? `${mode.label} von Favoriten lösen`
                                    : `${mode.label} als Favorit anheften`
                                }
                                title={pinned ? 'Lösen' : 'Anheften'}
                                className={hauptLook
                                  ? `mr-1.5 inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md border border-violet-400/40 ${pinned ? 'bg-violet-600 text-white' : 'bg-violet-600/30 text-violet-200 hover:bg-violet-600/55'}`
                                  : 'shrink-0 self-center mr-1.5 p-1.5 rounded-md text-slate-500 hover:text-violet-200 hover:bg-white/[0.06]'}
                              >
                                {hauptLook ? (
                                  <Star className={`h-3.5 w-3.5 ${pinned ? 'fill-current text-white' : ''}`} aria-hidden />
                                ) : pinned ? (
                                  <PinOff className="w-3.5 h-3.5" aria-hidden />
                                ) : (
                                  <Pin className="w-3.5 h-3.5" aria-hidden />
                                )}
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                      <p className="px-2.5 pt-1 pb-0.5 text-[10px] text-slate-600 leading-snug">
                        Paragraphen-Suche bleibt in der Suchleiste — kein zweites Analysieren.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Paragraphen-Suchleiste: immer sichtbar — Nachschlagen, §-Favoriten, Related */}
              <ArkoComposerSearchChip
                attached={attachedParagraphs}
                onAttachedChange={onAttachedParagraphsChange ?? (() => {})}
                onLookup={onParagraphLookup ?? (() => {})}
                relatedNorms={relatedParagraphs}
                disabled={isLoading || !onParagraphLookup || !onAttachedParagraphsChange}
              />
              {onJudicatureLookup ? (
                <ArkoComposerJudSearchChip
                  onLookup={onJudicatureLookup}
                  disabled={isLoading}
                />
              ) : null}
              {onWebSearchLookup ? (
                <ArkoComposerWebSearchChip
                  onLookup={onWebSearchLookup}
                  disabled={isLoading}
                />
              ) : null}

              {/* Angepinnte Modus-/Werkzeug-Favoriten (zusätzlich zur Suchleiste) */}
              {pinnedIds.map((pinId) => {
                const meta = getComposerPinMeta(pinId);
                if (!meta) return null;
                const selected =
                  (meta.kind === 'mode' && meta.modeId === activeModeId) ||
                  (meta.kind === 'tool' && meta.intent === activeToolMode?.intent);
                return (
                  <span
                    key={pinId}
                    className={`inline-flex items-center gap-0.5 rounded-full border pl-2.5 pr-0.5 py-0.5 text-[11px] font-medium ${
                      selected
                        ? hauptLook
                          ? 'border-violet-400/40 bg-violet-600 text-white'
                          : 'border-violet-500/40 bg-violet-500/10 text-white ring-1 ring-violet-400/25'
                        : hauptLook
                          ? 'border-violet-400/40 bg-[#07070a] text-violet-100'
                          : 'border-white/[0.08] bg-slate-900/70 text-slate-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => activatePinned(pinId)}
                      disabled={isLoading}
                      className="hover:text-white disabled:opacity-40"
                      aria-pressed={selected}
                      aria-label={`${meta.label} aktivieren`}
                    >
                      {meta.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTogglePin(pinId)}
                      className="p-1 rounded-full text-slate-500 hover:text-slate-200 hover:bg-white/[0.06]"
                      aria-label={`${meta.label} lösen`}
                      title="Lösen"
                    >
                      <X className="w-3 h-3" aria-hidden />
                    </button>
                  </span>
                );
              })}

              {/* Werkzeuge-Reiter */}
              {hasTools ? (
                <div className="relative" ref={werkzeugeRootRef}>
                  {hauptLook ? (
                    <HauptTrayFlip
                      open={openTray === 'werkzeuge'}
                      label="Werkzeuge"
                      onClick={() => toggleTray('werkzeuge')}
                      testId="portal-chip-werkzeuge"
                      expanded="true"
                    />
                  ) : (
                  <button
                    type="button"
                    data-testid="portal-chip-werkzeuge"
                    onClick={() => toggleTray('werkzeuge')}
                    aria-expanded={openTray === 'werkzeuge'}
                    aria-haspopup="true"
                    className={openTray === 'werkzeuge' ? pillOn : pill}
                  >
                    Werkzeuge
                    <ChevronDown
                      className={`w-3 h-3 shrink-0 transition-transform ${
                        openTray === 'werkzeuge' ? 'rotate-180' : ''
                      }`}
                      aria-hidden
                    />
                  </button>
                  )}
                  {openTray === 'werkzeuge' ? (
                    <div className={`${trayPopoverClass} min-w-[min(100vw-2rem,24rem)]`}>
                      <div className="p-2">{toolsBarWithPins}</div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {canAttachFiles ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || attachments.length >= PORTAL_CHAT_ANALYZE_MAX_FILES}
                  className={pill}
                  title="Dokumente einfügen (PDF, Word, …)"
                  aria-label="Dokumente einfügen"
                >
                  <Paperclip className="w-3 h-3 shrink-0" aria-hidden />
                  {hauptLook ? null : <span className="hidden sm:inline">Dokumente</span>}
                </button>
              ) : null}
            </div>

            <div className="flex items-center gap-2 ml-auto shrink-0">
              {contextUsage && onContinueAkte && contextUsage.percent >= 90 ? (
                <button
                  type="button"
                  onClick={onContinueAkte}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-100 hover:bg-rose-500/20"
                  title="Kontext voll — Arbeitsstand verdichten und neuen Chat in dieser Akte starten"
                >
                  <Redo2 className="w-3 h-3" aria-hidden />
                  Weiterarbeiten
                </button>
              ) : null}
              {contextUsage ? <ArkoContextMeter usage={contextUsage} /> : null}
              <span className="text-[10px] text-slate-500 hidden sm:inline whitespace-nowrap">
                {isLoading ? 'Abbrechen' : 'Enter senden'}
              </span>
              {isLoading ? (
                <button
                  type="button"
                  onClick={() => onCancel?.()}
                  className={`flex items-center justify-center rounded-md p-2 ${hauptLook ? 'bg-violet-600 text-white hover:bg-violet-500' : CURSOR_PORTAL.chipActive}`}
                  aria-label="Anfrage abbrechen"
                  title="Abbrechen"
                >
                  <Square className="w-4 h-4 fill-current" aria-hidden />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`flex items-center justify-center rounded-md p-2 disabled:opacity-30 disabled:cursor-not-allowed ${hauptLook ? 'bg-violet-600 text-white hover:bg-violet-500' : CURSOR_PORTAL.chipActive}`}
                  aria-label="Nachricht senden"
                >
                  <ArrowUp className="w-4 h-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    );

    const columnPad = compact ? 'px-2' : 'px-3 sm:px-4';

    if (isBottom) {
      return (
        <div className={`${ARKO_CHAT_COLUMN_CLASS} ${columnPad}`}>{composerBox}</div>
      );
    }

    return (
      <div
        className={`flex flex-col items-center justify-center w-full flex-1 min-h-0 ${
          compact ? 'py-3 gap-3' : 'py-6 md:py-10 gap-5'
        }`}
      >
        {showLanding && (
          <header className="text-center max-w-lg animate-fade-in flex-shrink-0 px-3">
            <div className="inline-flex items-center gap-2 mb-3 text-slate-500">
              <Sparkles className="w-4 h-4 text-violet-400" aria-hidden />
              <span className="text-[11px] font-medium uppercase tracking-wider">ARKO</span>
            </div>
            <h1 className="text-xl md:text-2xl font-medium text-white tracking-tight">
              Guten Tag, <span className="text-slate-400">{firstName}</span>
            </h1>
            {kanzlei ? <p className="text-sm text-slate-500 mt-1">{kanzlei}</p> : null}
          </header>
        )}

        <div className={`${ARKO_CHAT_COLUMN_CLASS} ${columnPad} flex-shrink-0`}>{composerBox}</div>

        {!compact && (
          <p className="text-[11px] text-slate-500 text-center max-w-md leading-relaxed">
            Keine Rechtsberatung · Quellenpflicht bei Rechtsfragen · Daten bleiben in Ihrer Kanzlei
          </p>
        )}
      </div>
    );
  },
);

ArkoCursorComposer.displayName = 'ArkoCursorComposer';

/** Suchfeld für Cursor-Rail (Thread-Liste). */
export const ArkoCursorThreadSearch: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  tone?: 'default' | 'akte';
}> = ({ value, onChange, placeholder = 'Chats suchen…', tone = 'default' }) => (
  <div className="relative">
    <Search className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-500 ${tone === 'akte' ? 'left-2 h-3 w-3' : 'left-2.5 w-3.5 h-3.5'}`} aria-hidden />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label="Chats suchen"
      className={
        tone === 'akte'
          ? 'w-full rounded-md border border-violet-400/40 bg-[#07070a] py-1 pl-7 pr-2 text-[11px] text-slate-200 placeholder:text-slate-600 outline-none focus:border-violet-400'
          : 'w-full rounded-lg border border-white/[0.08] bg-slate-900/80 pl-8 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-violet-500/35'
      }
    />
  </div>
);
